import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "How tests uncover hidden complexity in simple code",
  "description": "",
  "published": "2020-04-14T00:00:00.000Z",
  "redirect_from": ["/blog/how-tests-uncover-hidden-complexity-in-simple-code/swizec/9371"],
  "categories": "Computer Science, Front End, Technical",
  "hero": "./img/techletter.app-screenshot-1586877415543.png"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <blockquote>
      <p parentName="blockquote">{`"Hey Bob, we need a function that returns the current environment name so QA can test our apps"`}</p>
    </blockquote>
    <p>{`Okay that's easy enough.`}</p>
    <p>{`You're building a utility method for every project. Something that checks an `}<inlineCode parentName="p">{`env=`}</inlineCode>{` param and lets you run web apps against production, QA, and local environments.`}</p>
    <p><a parentName="p" {...{
        "href": "https://carbon.now.sh/?bg=rgba(255,255,255,1)&t=seti&l=typescript&ds=true&wc=true&wa=true&pv=48px&ph=32px&ln=false&code=class%20EnvironmentHelper%20%7B%0A%20%20%20%20get%20currentEnvironment()%3A%20string%20%7C%20null%20%7B%0A%20%20%20%20%20%20%20%20%09let%20env%20%3D%20null%3B%0A%20%20%20%20%20%20%20%20if%20(typeof%20window%20!%3D%3D%20%22undefined%22)%20%7B%0A%20%20%20%20%20%20%20%20%20%20%20%20env%20%3D%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20new%20URLSearchParams(window.location.search).get(%22env%22)%3B%0A%20%20%20%20%20%20%20%20%7D%0A%0A%20%20%20%20%20%20%20%20return%20env%3B%0A%20%20%20%20%7D"
      }}><figure parentName="a" {...{
          "className": "gatsby-resp-image-figure",
          "style": {
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
    `}<span parentName="figure" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "670px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "52.017937219730946%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'208\\'%20viewBox=\\'0%200%20400%20208\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M19%2030v76l1%2074h361V30l-181-1-181%201\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/6cf308c97eceb065f43190c7acaa6c6d/ca0a1/techletter.app-screenshot-1586877415543.webp 223w", "/static/6cf308c97eceb065f43190c7acaa6c6d/75680/techletter.app-screenshot-1586877415543.webp 445w", "/static/6cf308c97eceb065f43190c7acaa6c6d/6cceb/techletter.app-screenshot-1586877415543.webp 670w"],
                "sizes": "(max-width: 670px) 100vw, 670px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/6cf308c97eceb065f43190c7acaa6c6d/e92b6/techletter.app-screenshot-1586877415543.png 223w", "/static/6cf308c97eceb065f43190c7acaa6c6d/e66bf/techletter.app-screenshot-1586877415543.png 445w", "/static/6cf308c97eceb065f43190c7acaa6c6d/d67fd/techletter.app-screenshot-1586877415543.png 670w"],
                "sizes": "(max-width: 670px) 100vw, 670px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/6cf308c97eceb065f43190c7acaa6c6d/d67fd/techletter.app-screenshot-1586877415543.png",
                "alt": "Click through for source",
                "title": "Click through for source",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
            "className": "gatsby-resp-image-figcaption"
          }}>{`Click through for source`}</figcaption>{`
  `}</figure></a></p>
    <p><inlineCode parentName="p">{`URLSearchParams`}</inlineCode>{` parses the `}<inlineCode parentName="p">{`window.location.search`}</inlineCode>{` string into a Map and you `}<inlineCode parentName="p">{`.get()`}</inlineCode>{` the `}<inlineCode parentName="p">{`env`}</inlineCode>{` param. If the param isn't there, the value is `}<inlineCode parentName="p">{`null`}</inlineCode>{`.`}</p>
    <p>{`Your spidey senses were tingling so you made sure `}<inlineCode parentName="p">{`window`}</inlineCode>{` is defined. Lets you run this code in server-side rendering.`}</p>
    <p>{`Job well done 👌`}</p>
    <blockquote>
      <p parentName="blockquote">{`"Hey Bob, would be cool if we didn't need to add ?env=development on localhost and ?env=production on production"`}</p>
    </blockquote>
    <p>{`Damn, that `}<em parentName="p">{`would`}</em>{` be cool.`}</p>
    <p>{`You add some defaults.`}</p>
    <p><a parentName="p" {...{
        "href": "https://carbon.now.sh/?bg=rgba(255,255,255,1)&t=seti&l=typescript&ds=true&wc=true&wa=true&pv=48px&ph=32px&ln=false&code=class%20EnvironmentHelper%20%7B%0A%20%20%20%20private%20get%20defaultEnvironment()%20%7B%0A%20%20%20%20%20%20%20%20return%20window.location.hostname.endsWith(%22company.com%22)%0A%20%20%20%20%20%20%20%20%20%20%20%20%3F%20%22production%22%0A%20%20%20%20%20%20%20%20%20%20%20%20%3A%20%22development%22%3B%0A%20%20%20%20%7D%0A%20%20%20%20%0A%20%20%20%20get%20currentEnvironment()%3A%20string%20%7B%0A%20%20%20%20%20%20%20%20%09let%20env%20%3D%20null%3B%0A%20%20%20%20%20%20%20%20if%20(typeof%20window%20!%3D%3D%20%22undefined%22)%20%7B%0A%20%20%20%20%20%20%20%20%20%20%20%20env%20%3D%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20new%20URLSearchParams(window.location.search).get(%22env%22)%3B%0A%20%20%20%20%20%20%20%20%7D%0A%0A%20%20%20%20%20%20%20%20return%20env%20%7C%7C%20this.defaultEnvironment%3B%0A%20%20%20%20%7D"
      }}><figure parentName="a" {...{
          "className": "gatsby-resp-image-figure",
          "style": {
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
    `}<span parentName="figure" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "670px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "68.16143497757847%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'273\\'%20viewBox=\\'0%200%20400%20273\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M20%2030c-2%201-1%20212%200%20214%202%202%20358%202%20360%200l1-108V30l-181-1-180%201\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/49f2a7a3a5ea81c6b663ac557c5c01b7/ca0a1/techletter.app-screenshot-1586877415495.webp 223w", "/static/49f2a7a3a5ea81c6b663ac557c5c01b7/75680/techletter.app-screenshot-1586877415495.webp 445w", "/static/49f2a7a3a5ea81c6b663ac557c5c01b7/6cceb/techletter.app-screenshot-1586877415495.webp 670w"],
                "sizes": "(max-width: 670px) 100vw, 670px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/49f2a7a3a5ea81c6b663ac557c5c01b7/e92b6/techletter.app-screenshot-1586877415495.png 223w", "/static/49f2a7a3a5ea81c6b663ac557c5c01b7/e66bf/techletter.app-screenshot-1586877415495.png 445w", "/static/49f2a7a3a5ea81c6b663ac557c5c01b7/d67fd/techletter.app-screenshot-1586877415495.png 670w"],
                "sizes": "(max-width: 670px) 100vw, 670px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/49f2a7a3a5ea81c6b663ac557c5c01b7/d67fd/techletter.app-screenshot-1586877415495.png",
                "alt": "Click through for source",
                "title": "Click through for source",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
            "className": "gatsby-resp-image-figcaption"
          }}>{`Click through for source`}</figcaption>{`
  `}</figure></a></p>
    <p>{`That wasn't so hard. If there's no param, pick a default value based on the current domain. `}<inlineCode parentName="p">{`"production"`}</inlineCode>{` in production and `}<inlineCode parentName="p">{`"development"`}</inlineCode>{` everywhere else.`}</p>
    <p>{`Going to break in QA environments and that's okay. The QA team knows to add `}<inlineCode parentName="p">{`?env=qa`}</inlineCode>{` when testing.`}</p>
    <blockquote>
      <p parentName="blockquote">{`"Hey Bob, you know what would be really cool? If we remembered the environment even if someone drops the param by accident"`}</p>
    </blockquote>
    <p><img parentName="p" {...{
        "src": "/95b2a19558cfa70cba99020f92c7e510/media-xT0xeJpnrWC4XWblEk-giphy-downsized-medium.gif",
        "alt": "mind_blown giphy"
      }}></img></p>
    <p>{`That's a `}<em parentName="p">{`great`}</em>{` idea!`}</p>
    <p>{`You `}<a parentName="p" {...{
        "href": "https://www.npmjs.com/package/js-cookie"
      }}>{`npm install js-cookie`}</a>{` and get to work.`}</p>
    <p><a parentName="p" {...{
        "href": "https://carbon.now.sh/?bg=rgba(255,255,255,1)&t=seti&l=typescript&ds=true&wc=true&wa=true&pv=48px&ph=32px&ln=false&code=import%20Cookies%20from%20%22js-cookie%22%3B%0A%0Aclass%20EnvironmentHelper%20%7B%0A%20%20%20%20private%20get%20defaultEnvironment()%20%7B%0A%20%20%20%20%20%20%20%20return%20window.location.hostname.endsWith(%22company.com%22)%0A%20%20%20%20%20%20%20%20%20%20%20%20%3F%20%22production%22%0A%20%20%20%20%20%20%20%20%20%20%20%20%3A%20%22development%22%3B%0A%20%20%20%20%7D%0A%20%20%20%20%0A%20%20%20%20get%20currentEnvironment()%3A%20string%20%7B%0A%20%20%20%20%20%20%20%20%09let%20env%20%3D%20Cookies.get(%22env%22)%3B%0A%20%20%20%20%20%20%20%20%09%0A%20%20%20%20%20%20%20%20if%20(typeof%20window%20!%3D%3D%20%22undefined%22)%20%7B%0A%20%20%20%20%20%20%20%20%20%20%20%20env%20%3D%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20new%20URLSearchParams(window.location.search).get(%22env%22)%3B%0A%20%20%20%20%20%20%20%20%7D%0A%20%20%20%20%20%20%20%20%0A%20%20%20%20%20%20%20%20if%20(env%20!%3D%3D%20Cookies.get(%22env%22))%20%7B%0A%20%20%20%20%20%20%20%20%20%20%20%20Cookies.set(%22env%22%2C%20env)%3B%0A%20%20%20%20%20%20%20%20%7D%0A%0A%20%20%20%20%20%20%20%20return%20env%20%7C%7C%20this.defaultEnvironment%3B%0A%20%20%20%20%7D"
      }}><figure parentName="a" {...{
          "className": "gatsby-resp-image-figure",
          "style": {
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
    `}<span parentName="figure" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "670px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "86.99551569506727%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'348\\'%20viewBox=\\'0%200%20400%20348\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M20%2030c-2%201-1%20287%200%20289%202%202%20358%202%20360%200l1-146V30l-181-1-180%201\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/15cbdd5c55e3f7d78ac34f903ec3039a/ca0a1/techletter.app-screenshot-1586877415484.webp 223w", "/static/15cbdd5c55e3f7d78ac34f903ec3039a/75680/techletter.app-screenshot-1586877415484.webp 445w", "/static/15cbdd5c55e3f7d78ac34f903ec3039a/6cceb/techletter.app-screenshot-1586877415484.webp 670w"],
                "sizes": "(max-width: 670px) 100vw, 670px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/15cbdd5c55e3f7d78ac34f903ec3039a/e92b6/techletter.app-screenshot-1586877415484.png 223w", "/static/15cbdd5c55e3f7d78ac34f903ec3039a/e66bf/techletter.app-screenshot-1586877415484.png 445w", "/static/15cbdd5c55e3f7d78ac34f903ec3039a/d67fd/techletter.app-screenshot-1586877415484.png 670w"],
                "sizes": "(max-width: 670px) 100vw, 670px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/15cbdd5c55e3f7d78ac34f903ec3039a/d67fd/techletter.app-screenshot-1586877415484.png",
                "alt": "Click through for source",
                "title": "Click through for source",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
            "className": "gatsby-resp-image-figcaption"
          }}>{`Click through for source`}</figcaption>{`
  `}</figure></a></p>
    <p>{`You read the `}<inlineCode parentName="p">{`env`}</inlineCode>{` cookie, check the URL param, and update the cookie when those don't match. Return the environment or default based on domain.`}</p>
    <p>{`Job well done!`}</p>
    <p>{`Function's getting a little complicated, but eh it's still simple enough right? Easy to reason about.`}</p>
    <p>{`Deploy 🚀`}</p>
    <h2 {...{
      "id": "hidden-complexity-in-simple-code"
    }}>{`Hidden complexity in simple code`}</h2>
    <p>{`You get bug reports. Something's fishy.`}</p>
    <p>{`Works okay on localhost, seems fine enough in production, but QA environments are acting weird. On every 2nd page load, they start hitting production servers.`}</p>
    <p><img parentName="p" {...{
        "src": "/ebb5c946b0c0e37d9de0670cfbd376a0/media-cmqZq4YiaWAZQ0h4Rr-giphy.gif",
        "alt": "something_fishy giphy"
      }}></img></p>
    <p>{`This function ain't so simple after all. What did you miss?`}</p>
    <p>{`You have 3 pieces of state. Each can be set or not set. How many states is that?`}</p>
    <p>{`Bet you it's more than you think 😛`}</p>
    <p>{`Lemme show you a trick from college`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "890px",
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
      `}<a parentName="span" {...{
          "className": "gatsby-resp-image-link",
          "href": "/static/0a6a1e2bb1dd5b0b9e9f2319c3ac9503/67a79/zZNVUdJ.png",
          "style": {
            "display": "block"
          },
          "target": "_blank",
          "rel": "noopener"
        }}>{`
    `}<span parentName="a" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "78.9237668161435%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'315\\'%20viewBox=\\'0%200%20400%20315\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M254%208a757%20757%200%2001-5%2027l-2%208-4%201c-5%201-34%201-54-1l-40-1-26%201V7c-1%201-2%208-2%2018v18h-15l-18%202a956%20956%200%200123%200h10v5a386%20386%200%2000-4%2033%20538%20538%200%2000-5%2074c-1%2019-1%2028%201%2061%201%2019%201%2020-1%2039-3%2023-2%2053%201%2053l1-2c-2-7-2-36%200-53%201-15%202-20%201-34-2-40-2-50%200-73a479%20479%200%20004-63l3-28%201-12%2019-1c17-1%2033-1%2043%201%208%201%2049%202%2055%201h7v4l-9%2046c-3%2017-5%2027-6%2051l-2%2026a524%20524%200%20004-29c1-23%202-28%205-44a5216%205216%200%200010-52c0-3%204-3%2045-3%2038%201%2035%201%2096%208l5-1-5-1c-64-8-61-7-102-7h-39l1-4%204-25%202-10-2%203M150%2024l-1%203-1%201-1%202h10c1%202%202%202%206%201%207-2%208-4%206-6-3-1-6-1-8%201-1%201-1%201-2-1s-7-3-9-1M12%2031v6l7-1c7%200%2015-2%2017-5h3l4-1c4-3%204-3%204%202l1%205v-5c2-7%200-8-4-5-3%202-4%202-6%201s-2-1-3%201-1%202-3%200c-2-3-6-3-8%201l-2%202-3-3c-5-6-7-5-7%202m156%2030c-6%205-5%208%205%208%208%200%2012-6%207-9-3-3-7-2-12%201m115%200c-8%204-6%207%205%207%209%200%2012-4%206-8-3-3-5-2-11%201M44%2064c-5%203-6%206-1%207%208%203%2019-4%2014-9-2-2-8-1-13%202m123%2024l-2%203c-5%202-3%203%204%204%2010%200%2016-4%2010-7h-12M42%2090c-4%202-6%204-4%207%201%203%2013%202%2016-1l2-2-2-2c-3-3-6-4-12-2m232%2021c-7%204-6%2010%201%2011%208%200%2013-6%207-10-3-2-5-2-8-1m-234%207c-5%204-3%207%206%207%206%200%208-3%204-8-4-3-6-3-10%201m-2%2026c-7%206%200%209%209%205%207-3%206-7-2-8-3%200-4%200-7%203m224%2025l-2%203-1%201c-2%200-1%203%201%204%207%204%2014%203%2014-3%200-4-8-7-12-5m-104%202c-4%205-2%208%205%208%209%200%2010-5%202-9-4-1-4-1-7%201m-1%2030c-3%204-3%205%202%206%207%202%2013%200%2013-4%200-2-4-5-7-5-5-1-5-1-8%203m95%2026c-4%203-4%204%200%207%204%204%2012%201%2012-5%200-5-8-7-12-2\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/0a6a1e2bb1dd5b0b9e9f2319c3ac9503/ca0a1/zZNVUdJ.webp 223w", "/static/0a6a1e2bb1dd5b0b9e9f2319c3ac9503/75680/zZNVUdJ.webp 445w", "/static/0a6a1e2bb1dd5b0b9e9f2319c3ac9503/8d1ba/zZNVUdJ.webp 890w", "/static/0a6a1e2bb1dd5b0b9e9f2319c3ac9503/3838e/zZNVUdJ.webp 1335w", "/static/0a6a1e2bb1dd5b0b9e9f2319c3ac9503/51a5c/zZNVUdJ.webp 1408w"],
              "sizes": "(max-width: 890px) 100vw, 890px",
              "type": "image/webp"
            }}></source>{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/0a6a1e2bb1dd5b0b9e9f2319c3ac9503/e92b6/zZNVUdJ.png 223w", "/static/0a6a1e2bb1dd5b0b9e9f2319c3ac9503/e66bf/zZNVUdJ.png 445w", "/static/0a6a1e2bb1dd5b0b9e9f2319c3ac9503/4ef49/zZNVUdJ.png 890w", "/static/0a6a1e2bb1dd5b0b9e9f2319c3ac9503/4e814/zZNVUdJ.png 1335w", "/static/0a6a1e2bb1dd5b0b9e9f2319c3ac9503/67a79/zZNVUdJ.png 1408w"],
              "sizes": "(max-width: 890px) 100vw, 890px",
              "type": "image/png"
            }}></source>{`
          `}<img parentName="picture" {...{
              "className": "gatsby-resp-image-image",
              "src": "/static/0a6a1e2bb1dd5b0b9e9f2319c3ac9503/4ef49/zZNVUdJ.png",
              "alt": "zZNVUdJ",
              "title": "zZNVUdJ",
              "loading": "lazy",
              "style": {
                "width": "100%",
                "height": "100%",
                "margin": "0",
                "verticalAlign": "middle",
                "position": "absolute",
                "top": "0",
                "left": "0"
              }
            }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span></p>
    <p>{`You write a binary truth table. Each column is a variable, `}<inlineCode parentName="p">{`0`}</inlineCode>{` means empty, `}<inlineCode parentName="p">{`1`}</inlineCode>{` means set.`}</p>
    <p>{`2^3 gives you 8 different states to look at.`}</p>
    <p>{`Your function has just 4 conditionals, hmm 🤔`}</p>
    <p>{`We can discard some because of precedence rules. `}<inlineCode parentName="p">{`?env=`}</inlineCode>{` param always wins, so you can drop all but one row where it's set.`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "890px",
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
      `}<a parentName="span" {...{
          "className": "gatsby-resp-image-link",
          "href": "/static/6bed73965f5f780fe87f525584e23d3e/67a79/K3LySTD.png",
          "style": {
            "display": "block"
          },
          "target": "_blank",
          "rel": "noopener"
        }}>{`
    `}<span parentName="a" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "78.9237668161435%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'315\\'%20viewBox=\\'0%200%20400%20315\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M255%207a292%20292%200%2000-8%2036c-1%202-28%202-51%200l-46-1-27%201V7c-1%201-2%208-2%2018v18h-15l-18%202a870%20870%200%200121%200h12v5a318%20318%200%2000-4%2033%20559%20559%200%2000-4%2061l-30%202H53l-2-2c-3-4-8-4-13%200-2%202-5%203-7%203l-3%201h3l4%201c1%203%208%203%2012%200l35-2h31l-1%2010v45H88l-35%202c-14%201-13%201-11-4l2-5c-2-1-4%201-5%205-2%206-1%205-18%207l-10%201%202%201%2014-2c11%200%2011%200%2011%202l1%202%201-2%202-2%2052-4h18l1%204v45l-1%205H94l-36%201c-18%201-19%201-17-2v-3c0-1-3%203-3%205s-13%204-25%205l-6%201h13c17-3%2018-3%2018-1l1%203%201-2c0-3%201-3%205-3l55-3h12l-1%205c-2%2013%200%2047%202%2047v-50c1-2%203-2%2020-2h19v3l1%202%201-2c0-4%201-4%2038-4%2037-1%2034-2%2033%2012v6l1-10%201-8h23v4l1%204%201-4c0-4%200-4%2012-3%2022%202%2035%202%2035%201l-8-1-23-1c-15-1-15-1-14-4%200-3-2-3-3%201l-3%202-12-1h-11v-14l4-39%2016%201h14l-1%204c0%205%201%205%203%201l1-3h14a982%20982%200%200112-1c-24-1-25-1-25-4-1-3-2-3-3%200%200%202%200%202-24%200h-7v-3l3-27%202-24h6l18%201h14v3c0%204%202%203%202%200%201-3%205-4%2030-4l22-1h-51l1-2v-6l-2%204-1%204h-19l-20-1c-1-1%202-30%205-44a3147%203147%200%200010-52c0-3%206-3%2046-3%2032%201%2038%201%2067%204l33%203-5-1c-64-8-61-7-102-7h-39v-3l2-7%203-15%202-14-1%202M149%2026c-3%205-2%207%204%204h4c1%202%202%202%206%201%207-2%208-4%206-6-3-2-8-1-8%201-1%201-1%201-2-1-1-3-7-3-10%201M12%2031v6h7c7-1%2015-3%2017-6h3l4-1c4-3%204-3%204%202l1%205v-5c2-7%200-8-4-5-3%202-4%202-6%201s-2-1-3%201-1%202-3%200c-2-3-6-3-8%201l-2%202-3-3c-4-6-7-5-7%202m141%2013h-16l-14%201-1%2012-3%2024a484%20484%200%2000-4%2062%20507%20507%200%200042%201h10v-3c1-5%203-5%203-1v4h62v-7l5-39%2010-48v-4h-12c-12%201-33%200-49-1-10-1-32-2-33-1m15%2017c-6%205-5%208%205%208%208%200%2012-6%207-9-3-3-7-2-12%201m117-2l-4%202c-6%203-3%207%207%207%209%200%2012-4%206-8-2-2-7-3-9-1M44%2064c-6%204-6%206-1%207%208%203%2019-4%2014-9-2-2-8-1-13%202m123%2024l-2%203c-5%202-3%203%204%204%2010%200%2016-4%2010-7h-12M42%2090c-4%202-6%204-4%207%201%203%2013%202%2016-1l2-2-2-2c-3-3-6-4-12-2m232%2021c-7%204-6%2010%201%2011%208%200%2013-6%207-10-3-2-5-2-8-1m-234%206c-5%205-3%208%206%208%206%200%208-3%204-8-4-3-7-3-10%200m133%2029c-3%200-4%201-5%203%200%204-2%204-2%200l1-3h-17l-26%201h-9v4l-2%2046%201%205h13a578%20578%200%200132-3c0-3%207-2%2011%201h30c26%200%2027%200%2027-2a281%20281%200%20004-38l1-13c-1-2-4-2-28-2l-31%201m88%2023l-1%203-1%201c-2%200-1%203%201%205%207%204%2014%202%2014-4%200-4-10-8-13-5m-103%202c-4%205-2%207%205%209%208%201%2010-6%202-10-4-1-4-1-7%201m14%2032c0%202-4%205-7%205-4%200-10-2-10-4l-21-1h-20v5l1%2043-1%206h14c20%200%2023-1%2025-4%202-4%204-2%202%201l-1%202h12l35-1h23l-1-6c0-6%202-34%204-43v-4h-27l-28%201m80%2024c-4%203-3%206%201%208%205%202%2011-1%2011-6s-8-7-12-2\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/6bed73965f5f780fe87f525584e23d3e/ca0a1/K3LySTD.webp 223w", "/static/6bed73965f5f780fe87f525584e23d3e/75680/K3LySTD.webp 445w", "/static/6bed73965f5f780fe87f525584e23d3e/8d1ba/K3LySTD.webp 890w", "/static/6bed73965f5f780fe87f525584e23d3e/3838e/K3LySTD.webp 1335w", "/static/6bed73965f5f780fe87f525584e23d3e/51a5c/K3LySTD.webp 1408w"],
              "sizes": "(max-width: 890px) 100vw, 890px",
              "type": "image/webp"
            }}></source>{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/6bed73965f5f780fe87f525584e23d3e/e92b6/K3LySTD.png 223w", "/static/6bed73965f5f780fe87f525584e23d3e/e66bf/K3LySTD.png 445w", "/static/6bed73965f5f780fe87f525584e23d3e/4ef49/K3LySTD.png 890w", "/static/6bed73965f5f780fe87f525584e23d3e/4e814/K3LySTD.png 1335w", "/static/6bed73965f5f780fe87f525584e23d3e/67a79/K3LySTD.png 1408w"],
              "sizes": "(max-width: 890px) 100vw, 890px",
              "type": "image/png"
            }}></source>{`
          `}<img parentName="picture" {...{
              "className": "gatsby-resp-image-image",
              "src": "/static/6bed73965f5f780fe87f525584e23d3e/4ef49/K3LySTD.png",
              "alt": "K3LySTD",
              "title": "K3LySTD",
              "loading": "lazy",
              "style": {
                "width": "100%",
                "height": "100%",
                "margin": "0",
                "verticalAlign": "middle",
                "position": "absolute",
                "top": "0",
                "left": "0"
              }
            }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span></p>
    <p>{`Same is true for cookie. It always wins over the domain.`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "890px",
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
      `}<a parentName="span" {...{
          "className": "gatsby-resp-image-link",
          "href": "/static/acd1a055c09064e99e6f11b350cb100e/67a79/Ei311cv.png",
          "style": {
            "display": "block"
          },
          "target": "_blank",
          "rel": "noopener"
        }}>{`
    `}<span parentName="a" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "78.9237668161435%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'315\\'%20viewBox=\\'0%200%20400%20315\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M255%207a292%20292%200%2000-8%2036c-1%202-27%202-49%200h-75V7c-1%201-2%208-2%2018v18h-15l-18%202a840%20840%200%200121%200h12v4l-2%2014-2%2020a552%20552%200%2000-4%2061l-30%202H53l-2-2c-3-4-8-4-13%200-2%202-5%203-7%203l-3%201h3l4%201c1%202%208%203%2012%200l35-2h31l-1%2010v45H88l-35%202c-14%201-13%201-11-4l2-5c-2-1-4%201-5%205-2%206-1%205-18%207l-10%201%202%201%2014-2c11%200%2011%200%2011%202l1%202%201-2%202-2%2052-4h18v4l1%2014%201%209H99c-50%202-59%202-58%200v-6l-3%204-1%204H25c-11%201-15%201-15%203h2l14-2h11v3c1%204%202%205%203%200v-3h34l37-2h3v10l-2%2013v3H94a697%20697%200%2000-54%202l1-3v-3c0-1-3%203-3%205s-13%204-25%205l-6%201h13c17-3%2018-3%2018-1l1%203%201-2c0-3%201-3%205-3l55-3h12l-1%205c-2%2013%200%2047%202%2047v-50c1-2%203-2%2020-2h19v3l1%202%201-2c0-4%201-4%2038-4%2037-1%2034-2%2033%2012v7l1-10%201-9h23v4l1%204%201-4c0-4%200-4%2012-3%2022%202%2035%202%2035%201l-8-1-23-1c-15-1-15-1-14-4%200-3-2-3-3%201l-3%202-12-1h-11v-8l1-15v-6h13c13%200%2013%200%2012%202-3%202-2%205%202%207%205%202%2011-1%2011-6%200-2%200-2%203-1a353%20353%200%2000-24-4h-17l3-22%2016%201h14l-1%204c0%205%201%205%203%201l1-3h14a982%20982%200%200113-1c-24-1-26-2-26-4-1-3-2-3-3%200%200%202%200%202-24%200h-7v-3l3-27%202-24h6l18%201h14v3c0%204%202%203%202%200%201-3%205-4%2030-4l22-1h-51l1-2v-6l-2%204-1%204h-13l-19-1h-7v-9l5-37a1988%201988%200%200010-50c0-3%203-3%2038-3%2031%200%2039%201%2083%205l25%202-5-1c-64-8-61-7-102-7h-39l1-5a355%20355%200%20016-34l-1%202M149%2026c-3%205-2%207%204%204h4c1%202%202%202%206%201%207-2%208-4%206-6-3-2-8-1-8%201-1%201-1%201-2-1-1-3-7-3-10%201M12%2031v6h7c7-1%2015-3%2017-6h3l4-1c4-3%204-3%204%202s2%203%202-3c0-4-2-5-5-2-3%202-4%202-6%201s-2-1-3%201-1%202-3%200c-2-3-6-3-8%201l-2%202-3-3c-4-6-7-5-7%202m141%2013h-16l-14%201-1%2012-3%2024a496%20496%200%2000-4%2062c0%202%2015%203%2040%201h12v-3c1-5%203-5%203-1v4h62v-7l5-39%2010-48v-4l-7%201c-7%200-46-1-55-3h-32m15%2017c-6%205-5%208%205%208%208%200%2012-6%207-9-3-3-7-2-12%201m117-2l-4%202c-6%203-3%207%207%207%209%200%2012-4%206-8-2-2-7-3-9-1M44%2064c-5%203-6%205-3%207%205%203%2016-1%2017-5%201-6-7-7-14-2m123%2024l-2%203c-5%202-3%203%204%204%2010%200%2016-4%2010-7h-12M42%2090c-4%202-6%204-4%207%201%203%2013%202%2016-1l2-2-2-2c-3-3-6-4-12-2m232%2021c-7%204-6%2010%201%2011%208%200%2013-6%207-10-3-2-5-2-8-1m-234%206c-5%205-3%208%206%208%206%200%208-3%204-8-4-3-7-3-10%200m133%2029c-3%200-4%200-5%203%200%204-2%204-2%200l1-3h-17l-26%201h-9v4l-2%2046%201%205h11a506%20506%200%200134-3c0-3%207-2%2011%201h30c26%200%2027%200%2027-2a284%20284%200%20004-39v-13h-58m88%2023l-1%203-1%201c-2%200-1%203%201%205%207%204%2014%202%2014-4%200-4-10-8-13-5m-103%202c-4%205-2%207%205%209%208%201%2010-6%202-10-4-1-4-1-7%201m14%2032c0%202-4%205-7%205-4%200-10-2-10-4l-21-1h-20v4l1%2013v9h21c17-1%2021-1%2022-3h24a785%20785%200%200139-2h4v-7l2-11v-4h-27l-28%201m26%2024l-26%201-13%201-1%204c-2%207-2%208-3%205l1-6%201-3-14%201-20%201-7%201v7l-2%2012v6h14c20%200%2023-1%2025-4%202-4%204-2%202%201l-1%202h13l35-1h22v-29h-6l-20%201\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/acd1a055c09064e99e6f11b350cb100e/ca0a1/Ei311cv.webp 223w", "/static/acd1a055c09064e99e6f11b350cb100e/75680/Ei311cv.webp 445w", "/static/acd1a055c09064e99e6f11b350cb100e/8d1ba/Ei311cv.webp 890w", "/static/acd1a055c09064e99e6f11b350cb100e/3838e/Ei311cv.webp 1335w", "/static/acd1a055c09064e99e6f11b350cb100e/51a5c/Ei311cv.webp 1408w"],
              "sizes": "(max-width: 890px) 100vw, 890px",
              "type": "image/webp"
            }}></source>{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/acd1a055c09064e99e6f11b350cb100e/e92b6/Ei311cv.png 223w", "/static/acd1a055c09064e99e6f11b350cb100e/e66bf/Ei311cv.png 445w", "/static/acd1a055c09064e99e6f11b350cb100e/4ef49/Ei311cv.png 890w", "/static/acd1a055c09064e99e6f11b350cb100e/4e814/Ei311cv.png 1335w", "/static/acd1a055c09064e99e6f11b350cb100e/67a79/Ei311cv.png 1408w"],
              "sizes": "(max-width: 890px) 100vw, 890px",
              "type": "image/png"
            }}></source>{`
          `}<img parentName="picture" {...{
              "className": "gatsby-resp-image-image",
              "src": "/static/acd1a055c09064e99e6f11b350cb100e/4ef49/Ei311cv.png",
              "alt": "Ei311cv",
              "title": "Ei311cv",
              "loading": "lazy",
              "style": {
                "width": "100%",
                "height": "100%",
                "margin": "0",
                "verticalAlign": "middle",
                "position": "absolute",
                "top": "0",
                "left": "0"
              }
            }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span></p>
    <p>{`We're down to 4 states and 4 conditionals. Should be fine now, right?`}</p>
    <blockquote>
      <p parentName="blockquote">{`Beware of bugs in the above code; I have only proved it correct, not tried it. ~ Donald Knuth`}</p>
    </blockquote>
    <h3 {...{
      "id": "test-the-code"
    }}>{`Test the code!`}</h3>
    <p>{`What the heck's still missing? Let's add some tests. More tests than you think.`}</p>
    <p><a parentName="p" {...{
        "href": "https://carbon.now.sh/?bg=rgba(255,255,255,1)&t=seti&l=typescript&ds=true&wc=true&wa=true&pv=48px&ph=32px&ln=false&code=describe(%22currentEnvironment%22%2C%20()%20%3D%3E%20%7B%0A%20%20%20%20describe(%22no%20cookie%2C%20no%20url%20param%22%2C%20()%20%3D%3E%20%7B%0A%20%20%20%20%20%20%20%20it(%22returns%20development%20for%20localhost%22%2C%20()%20%3D%3E%20%7B%0A%20%20%20%20%20%20%20%20%20%20%20%20mockEnvCookie(%22%22)%3B%0A%20%20%20%20%20%20%20%20%20%20%20%20mockEnvParam(%22%22)%3B%0A%20%20%20%20%20%20%20%20%20%20%20%20window.location.hostname%20%3D%20%22localhost%22%3B%0A%20%20%20%20%20%20%20%20%20%20%20%20expect(EnvironmentHelper.currentEnvironment).toBe(%22development%22)%3B%0A%20%20%20%20%20%20%20%20%7D)%3B%0A%0A%20%20%20%20%20%20%20%20it(%22returns%20production%20for%20yup.com%22%2C%20()%20%3D%3E%20%7B%0A%20%20%20%20%20%20%20%20%20%20%20%20mockEnvCookie(%22%22)%3B%0A%20%20%20%20%20%20%20%20%20%20%20%20mockEnvParam(%22%22)%3B%0A%20%20%20%20%20%20%20%20%20%20%20%20window.location.hostname%20%3D%20%22company.com%22%3B%0A%20%20%20%20%20%20%20%20%20%20%20%20expect(EnvironmentHelper.currentEnvironment).toBe(%22production%22)%3B%0A%20%20%20%20%20%20%20%20%7D)%3B%0A%20%20%20%20%7D)%3B%0A%0A%20%20%20%20describe(%22no%20cookie%2C%20yes%20url%20param%22%2C%20()%20%3D%3E%20%7B%0A%20%20%20%20%20%20%20%20it(%22returns%20url%20param%22%2C%20()%20%3D%3E%20%7B%0A%20%20%20%20%20%20%20%20%20%20%20%20mockEnvCookie(%22%22)%3B%0A%20%20%20%20%20%20%20%20%20%20%20%20mockEnvParam(%22swizec%22)%3B%0A%20%20%20%20%20%20%20%20%20%20%20%20expect(EnvironmentHelper.currentEnvironment).toBe(%22swizec%22)%3B%0A%20%20%20%20%20%20%20%20%7D)%3B%0A%0A%20%20%20%20%20%20%20%20it(%22sets%20cookie%22%2C%20()%20%3D%3E%20%7B%0A%20%20%20%20%20%20%20%20%20%20%20%20mockEnvCookie(%22%22)%3B%0A%20%20%20%20%20%20%20%20%20%20%20%20mockEnvParam(%22swizec%22)%3B%0A%0A%20%20%20%20%20%20%20%20%20%20%20%20EnvironmentHelper.currentEnvironment%3B%0A%0A%20%20%20%20%20%20%20%20%20%20%20%20expect(Cookies.get(%22env%22)).toBe(%22swizec%22)%3B%0A%20%20%20%20%20%20%20%20%7D)%3B%0A%20%20%20%20%7D)%3B%0A%0A%20%20%20%20describe(%22yes%20cookie%2C%20no%20url%20param%22%2C%20()%20%3D%3E%20%7B%0A%20%20%20%20%20%20%20%20it(%22returns%20cookie%22%2C%20()%20%3D%3E%20%7B%0A%20%20%20%20%20%20%20%20%20%20%20%20mockEnvCookie(%22swizec%22)%3B%0A%20%20%20%20%20%20%20%20%20%20%20%20mockEnvParam(%22%22)%3B%0A%20%20%20%20%20%20%20%20%20%20%20%20expect(EnvironmentHelper.currentEnvironment).toBe(%22swizec%22)%3B%0A%20%20%20%20%20%20%20%20%7D)%3B%0A%20%20%20%20%7D)%3B%0A%0A%20%20%20%20describe(%22yes%20cookie%2C%20yes%20url%20param%22%2C%20()%20%3D%3E%20%7B%0A%20%20%20%20%20%20%20%20it(%22returns%20url%20param%22%2C%20()%20%3D%3E%20%7B%0A%20%20%20%20%20%20%20%20%20%20%20%20mockEnvCookie(%22swizec%22)%3B%0A%20%20%20%20%20%20%20%20%20%20%20%20mockEnvParam(%22development%22)%3B%0A%20%20%20%20%20%20%20%20%20%20%20%20expect(EnvironmentHelper.currentEnvironment).toBe(%22development%22)%3B%0A%20%20%20%20%20%20%20%20%7D)%3B%0A%0A%20%20%20%20%20%20%20%20it(%22updates%20cookie%22%2C%20()%20%3D%3E%20%7B%0A%20%20%20%20%20%20%20%20%20%20%20%20mockEnvCookie(%22swizec%22)%3B%0A%20%20%20%20%20%20%20%20%20%20%20%20mockEnvParam(%22development%22)%3B%0A%0A%20%20%20%20%20%20%20%20%20%20%20%20EnvironmentHelper.currentEnvironment%3B%0A%0A%20%20%20%20%20%20%20%20%20%20%20%20expect(Cookies.get(%22env%22)).toBe(%22development%22)%3B%0A%20%20%20%20%20%20%20%20%7D)%3B%0A%20%20%20%20%7D)%3B%0A%7D)%3B"
      }}><figure parentName="a" {...{
          "className": "gatsby-resp-image-figure",
          "style": {
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
    `}<span parentName="figure" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "718px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "171.30044843049328%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'686\\'%20viewBox=\\'0%200%20400%20686\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M19%2028a37653%2037653%200%20001%20631h361l1-316V28l-182-1-181%201\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/2f8b798d2d49742c05ebe06eaf298d31/ca0a1/techletter.app-screenshot-1586877415427.webp 223w", "/static/2f8b798d2d49742c05ebe06eaf298d31/75680/techletter.app-screenshot-1586877415427.webp 445w", "/static/2f8b798d2d49742c05ebe06eaf298d31/7d0c9/techletter.app-screenshot-1586877415427.webp 718w"],
                "sizes": "(max-width: 718px) 100vw, 718px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/2f8b798d2d49742c05ebe06eaf298d31/e92b6/techletter.app-screenshot-1586877415427.png 223w", "/static/2f8b798d2d49742c05ebe06eaf298d31/e66bf/techletter.app-screenshot-1586877415427.png 445w", "/static/2f8b798d2d49742c05ebe06eaf298d31/57dc1/techletter.app-screenshot-1586877415427.png 718w"],
                "sizes": "(max-width: 718px) 100vw, 718px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/2f8b798d2d49742c05ebe06eaf298d31/57dc1/techletter.app-screenshot-1586877415427.png",
                "alt": "Click through for source",
                "title": "Click through for source",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
            "className": "gatsby-resp-image-figcaption"
          }}>{`Click through for source`}</figcaption>{`
  `}</figure></a></p>
    <p>{`Yep, 7 tests for just 4 states. And I think that's still missing some helpful tests.`}</p>
    <p>{`So, what was the bug?`}</p>
    <h2 {...{
      "id": "🤦♀️"
    }}>{`🤦‍♀️`}</h2>
    <p>{`Bug's right here:`}</p>
    <p><a parentName="p" {...{
        "href": "https://carbon.now.sh/?bg=rgba(255,255,255,1)&t=seti&l=typescript&ds=true&wc=true&wa=true&pv=48px&ph=32px&ln=false&code=if%20(env%20!%3D%3D%20Cookies.get(%22env%22))%20%7B%0A%20%20%20%20Cookies.set(%22env%22%2C%20env)%3B%0A%7D"
      }}><figure parentName="a" {...{
          "className": "gatsby-resp-image-figure",
          "style": {
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
    `}<span parentName="figure" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "366px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "60.98654708520179%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'244\\'%20viewBox=\\'0%200%20400%20244\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M38%2054c-3%202-3%207-3%2071%200%2063%200%2064%202%2066%203%203%20324%203%20327%200%202-2%202-3%202-66%200-64%200-69-3-71H38m39%2017c-3%203-4%207-1%209%206%206%2014-2%209-8-2-3-5-3-8-1m20%202c-3%205%203%2011%208%208%202-2%203-3%203-5%200-6-7-8-11-3\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/da4837d06dc0c05d3ad8a33fb11cce28/ca0a1/techletter.app-screenshot-1586877415481.webp 223w", "/static/da4837d06dc0c05d3ad8a33fb11cce28/4146f/techletter.app-screenshot-1586877415481.webp 366w"],
                "sizes": "(max-width: 366px) 100vw, 366px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/da4837d06dc0c05d3ad8a33fb11cce28/e92b6/techletter.app-screenshot-1586877415481.png 223w", "/static/da4837d06dc0c05d3ad8a33fb11cce28/8b153/techletter.app-screenshot-1586877415481.png 366w"],
                "sizes": "(max-width: 366px) 100vw, 366px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/da4837d06dc0c05d3ad8a33fb11cce28/8b153/techletter.app-screenshot-1586877415481.png",
                "alt": "Click through for source",
                "title": "Click through for source",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
            "className": "gatsby-resp-image-figcaption"
          }}>{`Click through for source`}</figcaption>{`
  `}</figure></a></p>
    <p>{`That means we overwrite the cookie with an empty value when there's no URL param. Should look like this:`}</p>
    <p><a parentName="p" {...{
        "href": "https://carbon.now.sh/?bg=rgba(255,255,255,1)&t=seti&l=typescript&ds=true&wc=true&wa=true&pv=48px&ph=32px&ln=false&code=if%20(env%20%26%26%20env%20!%3D%3D%20Cookies.get(%22yup-env%22))%20%7B%0A%20%20%20%20Cookies.set(%22yup-env%22%2C%20env)%3B%0A%7D"
      }}><figure parentName="a" {...{
          "className": "gatsby-resp-image-figure",
          "style": {
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
    `}<span parentName="figure" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "454px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "49.327354260089685%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'196\\'%20viewBox=\\'0%200%20400%20196\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M30%2044c-3%202-3%20107%200%20109%202%203%20338%203%20340%200%203-2%203-107%200-109-2-3-338-3-340%200\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/9b763dd526dc300da98e93a0923d29b8/ca0a1/techletter.app-screenshot-1586877415231.webp 223w", "/static/9b763dd526dc300da98e93a0923d29b8/75680/techletter.app-screenshot-1586877415231.webp 445w", "/static/9b763dd526dc300da98e93a0923d29b8/44d9f/techletter.app-screenshot-1586877415231.webp 454w"],
                "sizes": "(max-width: 454px) 100vw, 454px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/9b763dd526dc300da98e93a0923d29b8/e92b6/techletter.app-screenshot-1586877415231.png 223w", "/static/9b763dd526dc300da98e93a0923d29b8/e66bf/techletter.app-screenshot-1586877415231.png 445w", "/static/9b763dd526dc300da98e93a0923d29b8/b3c1d/techletter.app-screenshot-1586877415231.png 454w"],
                "sizes": "(max-width: 454px) 100vw, 454px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/9b763dd526dc300da98e93a0923d29b8/b3c1d/techletter.app-screenshot-1586877415231.png",
                "alt": "Click through for source",
                "title": "Click through for source",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
            "className": "gatsby-resp-image-figcaption"
          }}>{`Click through for source`}</figcaption>{`
  `}</figure></a></p>
    <p>{`That simple function took 2 days off my life. All because I was too damn proud to write tests the first time.`}</p>
    <p>{`Happy coding ❤️`}</p>
    <p>{`Cheers,`}<br parentName="p"></br>{`
`}{`~Swizec`}</p>
    <p>{`PS: this situation adapted for brevity, IRL there were some other functions involved further obscuring the complexity`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      